import React, { useState, useEffect, useCallback } from 'react';
import Modal from 'react-modal';
import Select from 'react-select';
import api from '../services/api';
import Swal from 'sweetalert2';
import { FiTrash2, FiPlus } from 'react-icons/fi';

const DispensesModal = ({ isOpen, onClose, inscriptionId, inscriptionData }) => {
  const [dispenses, setDispenses] = useState([]);
  const [matieres, setMatieres] = useState([]);
  const [periodes, setPeriodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState({
    matiere_id: '',
    periode_id: null // null = dispense annuelle
  });

  const fetchDispenses = useCallback(async () => {
    if (!inscriptionId) return;
    try {
      const res = await api.get(`/dispenses-matieres/inscription/${inscriptionId}`);
      setDispenses(res.data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des dispenses:', error);
      Swal.fire('Erreur', 'Impossible de charger les dispenses', 'error');
    }
  }, [inscriptionId]);

  const fetchMatieres = useCallback(async () => {
    try {
      const res = await api.get('/matieres');
      setMatieres(res.data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des matières:', error);
    }
  }, []);

  const fetchPeriodes = useCallback(async () => {
    try {
      const res = await api.get('/periodes');
      setPeriodes(res.data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des périodes:', error);
    }
  }, []);

  useEffect(() => {
    if (isOpen && inscriptionId) {
      fetchDispenses();
      fetchMatieres();
      fetchPeriodes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, inscriptionId, fetchDispenses, fetchMatieres, fetchPeriodes]);

  const handleAdd = () => {
    setForm({ matiere_id: '', periode_id: null });
    setShowAddForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.matiere_id) {
      Swal.fire('Erreur', 'Veuillez sélectionner une matière', 'error');
      return;
    }

    setLoading(true);
    try {
      await api.post('/dispenses-matieres', {
        inscription_id: inscriptionId,
        matiere_id: form.matiere_id,
        periode_id: form.periode_id || null
      });
      await fetchDispenses();
      setShowAddForm(false);
      setForm({ matiere_id: '', periode_id: null });
      Swal.fire('Succès', 'Dispense ajoutée avec succès', 'success');
    } catch (error) {
      const message = error.response?.data?.message || 'Erreur lors de l\'ajout de la dispense';
      Swal.fire('Erreur', message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Supprimer cette dispense ?',
      text: 'Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/dispenses-matieres/${id}`);
        await fetchDispenses();
        Swal.fire('Supprimé !', 'La dispense a été supprimée.', 'success');
      } catch (error) {
        Swal.fire('Erreur', 'Impossible de supprimer la dispense', 'error');
      }
    }
  };

  const matieresOptions = matieres.map(m => ({
    value: m.id,
    label: m.lib_matiere || m.matiere || `Matière ${m.id}`
  }));

  const periodesOptions = [
    { value: null, label: 'Toute l\'année (toutes les périodes)' },
    ...periodes.map(p => ({
      value: p.id,
      label: p.periode || `Période ${p.id}`
    }))
  ];

  // Grouper les dispenses par matière
  const dispensesByMatiere = {};
  dispenses.forEach(d => {
    const key = d.matiere_id;
    if (!dispensesByMatiere[key]) {
      dispensesByMatiere[key] = {
        matiere: d.lib_matiere || d.abbrev_matiere || `Matière ${d.matiere_id}`,
        dispenses: []
      };
    }
    dispensesByMatiere[key].dispenses.push(d);
  });

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Gestion des dispenses"
      ariaHideApp={false}
      style={{
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          zIndex: 1000
        },
        content: {
          maxWidth: '800px',
          width: '100%',
          margin: 'auto',
          borderRadius: 12,
          padding: 0,
          maxHeight: '90vh',
          overflow: 'auto'
        }
      }}
    >
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            Gestion des dispenses
            {inscriptionData && (
              <span className="text-sm font-normal text-gray-600 ml-2">
                - {inscriptionData.nom} {inscriptionData.prenom}
              </span>
            )}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none"
            title="Fermer"
          >
            ×
          </button>
        </div>

        {/* Formulaire d'ajout */}
        {showAddForm && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
            <h3 className="text-lg font-medium mb-3">Ajouter une dispense</h3>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Matière *</label>
                  <Select
                    options={matieresOptions}
                    value={matieresOptions.find(o => o.value === form.matiere_id) || null}
                    onChange={opt => setForm({ ...form, matiere_id: opt ? opt.value : '' })}
                    isClearable
                    placeholder="Sélectionner une matière"
                    classNamePrefix="react-select"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Période</label>
                  <Select
                    options={periodesOptions}
                    value={periodesOptions.find(o => o.value === form.periode_id) || periodesOptions[0]}
                    onChange={opt => setForm({ ...form, periode_id: opt ? opt.value : null })}
                    placeholder="Sélectionner une période"
                    classNamePrefix="react-select"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Sélectionner "Toute l'année" pour une dispense annuelle
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {loading ? 'Ajout...' : 'Ajouter'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setForm({ matiere_id: '', periode_id: null });
                  }}
                  className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Bouton pour afficher le formulaire */}
        {!showAddForm && (
          <button
            onClick={handleAdd}
            className="mb-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
          >
            <FiPlus /> Ajouter une dispense
          </button>
        )}

        {/* Liste des dispenses */}
        <div className="mt-4">
          {Object.keys(dispensesByMatiere).length === 0 ? (
            <p className="text-gray-500 text-center py-8">Aucune dispense enregistrée</p>
          ) : (
            <div className="space-y-4">
              {Object.values(dispensesByMatiere).map((group, idx) => (
                <div key={idx} className="border rounded-lg p-4">
                  <h3 className="font-semibold text-lg mb-2">{group.matiere}</h3>
                  <div className="space-y-2">
                    {group.dispenses.map(d => (
                      <div
                        key={d.id}
                        className="flex items-center justify-between bg-white p-3 rounded border"
                      >
                        <div>
                          <span className="font-medium">
                            {d.periode_id === null
                              ? 'Toute l\'année (toutes les périodes)'
                              : `Période: ${d.periode || d.periode_abrev || `Période ${d.periode_id}`}`}
                          </span>
                        </div>
                        <button
                          onClick={() => handleDelete(d.id)}
                          className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-50"
                          title="Supprimer"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default DispensesModal;

